import React, { useState, useEffect } from 'react'

function ord(n) {
  n = parseInt(n)
  const s = (n === 1 || n === 21 || n === 31) ? 'st' : (n === 2 || n === 22) ? 'nd' : (n === 3 || n === 23) ? 'rd' : 'th'
  return n + s
}

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace',
  fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

export default function AddLoanModal({ loan, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '', type: 'mortgage', balance: '',
    original_amount: '', interest_rate: '',
    emi_amount: '', due_day: '', start_date: '', end_date: '',
    family_member: ''
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [showImportNotice, setShowImportNotice] = useState(false)

  useEffect(() => {
    if (loan) {
      setForm({
        name: loan.name || '',
        type: loan.type || 'mortgage',
        balance: loan.balance || '',
        original_amount: loan.original_amount || '',
        interest_rate: loan.interest_rate || '',
        emi_amount: loan.emi_amount || '',
        due_day: loan.due_day || '',
        start_date: loan.start_date ? loan.start_date.slice(0, 7) : '',
        end_date: loan.end_date ? loan.end_date.slice(0, 7) : '',
        family_member: loan.family_member || ''
      })
    }
  }, [loan])

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Please enter a loan name'); return }
    if (!form.balance || isNaN(form.balance)) { setError('Please enter a valid balance'); return }
    if (!form.interest_rate || isNaN(form.interest_rate)) { setError('Please enter a valid interest rate'); return }

    setSaving(true)
    setError('')
    await onSave({
      name: form.name.trim(),
      type: form.type,
      balance: parseFloat(form.balance),
      original_amount: parseFloat(form.original_amount) || parseFloat(form.balance),
      interest_rate: parseFloat(form.interest_rate),
      emi_amount: parseFloat(form.emi_amount) || 0,
      due_day: form.due_day ? parseInt(form.due_day) : null,
      start_date: form.start_date ? form.start_date + '-01' : null,
      end_date: form.end_date ? form.end_date + '-01' : null,
      family_member: form.family_member.trim() || null,
      ...(!loan && { joining_balance: parseFloat(form.balance) }),
    })
    setSaving(false)
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--modal-backdrop)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem'
      }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: '10px',
        border: '1px solid var(--border)',
        width: '100%', maxWidth: '520px',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)'
        }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700' }}>
            {loan ? 'Edit Loan' : 'Add Loan'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setShowImportNotice(v => !v)}
              style={{
                background: showImportNotice ? 'rgba(201,168,76,0.12)' : 'transparent',
                color: '#c9a84c',
                border: '1px solid rgba(201,168,76,0.4)',
                padding: '0.3rem 0.75rem',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.68rem',
                fontWeight: '600',
                cursor: 'pointer',
                letterSpacing: '0.03em',
                transition: 'all 0.15s',
              }}
            >
              ⚡ Quick Import
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem' }}>✕</button>
          </div>
        </div>

        {/* Quick Import notice */}
        {showImportNotice && (
          <div style={{
            margin: '1rem 1.5rem 0',
            background: 'rgba(201,168,76,0.07)',
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: '8px',
            padding: '0.9rem 1.1rem',
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '1.1rem', lineHeight: 1, flexShrink: 0, marginTop: '1px' }}>⚡</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '700', color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>
                Quick Import — Coming Soon
              </div>
              <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Paste any loan text from your bank statement, net banking portal, or loan document and let AI automatically fill in all the form fields. Requires an Anthropic API key to activate.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowImportNotice(false)}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0', flexShrink: 0 }}
            >✕</button>
          </div>
        )}

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {error && (
            <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#b8441a' }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            {/* Loan Name */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Loan Name</label>
              <input style={inputStyle} placeholder="e.g. SBI Home Loan" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>

            {/* Type */}
            <div>
              <label style={labelStyle}>Loan Type</label>
              <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="mortgage">Home Loan</option>
                <option value="auto">Auto Loan</option>
                <option value="student">Education Loan</option>
                <option value="personal">Personal Loan</option>
                <option value="credit">Credit Card</option>
              </select>
            </div>

            {/* Due Day */}
            <div>
              <label style={labelStyle}>EMI Due Day</label>
              <select style={inputStyle} value={form.due_day} onChange={e => set('due_day', e.target.value)}>
                <option value="">-- Select Day --</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{ord(d)} of every month</option>
                ))}
              </select>
            </div>

            {/* Balance */}
            <div>
              <label style={labelStyle}>Current Balance (₹)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="500000" value={form.balance} onChange={e => set('balance', e.target.value)} />
            </div>

            {/* Original Amount */}
            <div>
              <label style={labelStyle}>Original Amount (₹)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="600000" value={form.original_amount} onChange={e => set('original_amount', e.target.value)} />
            </div>

            {/* Interest Rate */}
            <div>
              <label style={labelStyle}>Interest Rate (% p.a.)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="8.5" value={form.interest_rate} onChange={e => set('interest_rate', e.target.value)} />
            </div>

            {/* EMI */}
            <div>
              <label style={labelStyle}>Monthly EMI (₹)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="9500" value={form.emi_amount} onChange={e => set('emi_amount', e.target.value)} />
            </div>

            {/* Start Date */}
            <div>
              <label style={labelStyle}>Start Date</label>
              <input style={inputStyle} type="month" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
            </div>

            {/* End Date */}
            <div>
              <label style={labelStyle}>End Date (payoff)</label>
              <input style={inputStyle} type="month" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
            </div>

            {/* Family Member */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Family Member (optional)</label>
              <input style={inputStyle} placeholder="e.g. Rahul, Priya, Self" value={form.family_member} onChange={e => set('family_member', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '0.9rem 1.5rem 1.3rem', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            {saving ? 'Saving...' : 'Save Loan'}
          </button>
        </div>
      </div>
    </div>
  )
}